import { Signal } from './index.js';

export type CleanupFn = () => void;

/**
 * Runs every effect whose sources have changed, now, rather than on the next
 * microtask.
 *
 * An effect is normally deferred so a burst of writes costs one run. A caller
 * that has to READ what an effect wrote — an imperative API on a component
 * whose markup is bound, asked for the state it has just set — cannot wait for
 * a microtask it is not allowed to await, and this is what it calls instead.
 * The queued microtask still fires and finds nothing pending.
 */
export function flushEffects(): void {
  Signal.subtle.flushEffects();
}

/**
 * Creates a reactive effect that re-runs whenever any signal it read during
 * the previous run changes. The callback may return a cleanup function,
 * which runs before the next re-run and when the effect is disposed.
 *
 * The effect is a node of the reactive graph: a write queues the effects that
 * read it and no others, so its cost does not grow with the number of effects
 * on the page.
 *
 * Canonical implementation - packages/ui's signal/effect.ts and
 * SignalGrid/utils/effect.js both re-export or wrap this.
 */
export function effect(callback: () => void | CleanupFn): CleanupFn {
  return Signal.subtle.effect(callback);
}

/**
 * A membership test that notifies only the keys whose answer changed.
 *
 * `const isSelected = selector(() => selected.get())` then `isSelected(row.id)`
 * in each row: moving the selection re-runs the two rows it left and entered,
 * where reading `selected` in every row would re-run them all.
 */
export function selector<K>(
  source: () => K,
): ((key: K) => boolean) & { dispose: CleanupFn } {
  const states = new Map<K, Signal.State<boolean>>();
  // One options object for every key: the key is found from the state, so a
  // key costs its state and its map entry, not a closure and options of its own.
  const keyOf = new WeakMap<object, K>();
  const options: Signal.Options<boolean> = {
    // Dropped once nothing reads it, so the map holds only live keys.
    [Signal.subtle.unwatched]() {
      if (!keyOf.has(this)) return;
      // SAFETY: has() answered true, so get() returns the K that set() stored;
      // K may itself include undefined, which is why has() is asked first.
      const key = keyOf.get(this) as K;
      if (states.get(key) === this) states.delete(key);
    },
  };
  let current = Signal.subtle.untrack(source);
  const stop = effect(() => {
    const next = source();
    if (Object.is(next, current)) return;
    const previous = current;
    current = next;
    Signal.subtle.untrack(() => {
      states.get(previous)?.set(false);
      states.get(next)?.set(true);
    });
  });
  const is = (key: K): boolean => {
    let state = states.get(key);
    if (!state) {
      state = new Signal.State(Object.is(key, current), options);
      keyOf.set(state, key);
      states.set(key, state);
    }
    return state.get();
  };
  return Object.assign(is, { dispose: stop });
}
